import axios from "axios";

export const SESSION_EXPIRED_EVENT = "session-expired";
export const ACCESS_BLOCKED_EVENT = "access-blocked";

// Code renvoyé par nginx quand l'adresse IP appelante n'est pas dans la liste blanche
// du vhost admin. Le corps de la réponse vient de nginx, pas d'admin-server.
const IP_NOT_ALLOWED_CODE = "IP_NOT_ALLOWED";

const GENERIC_MESSAGE =
  "Une erreur inattendue est survenue, merci de contacter un administrateur";

let isSessionExpired = false;

// Dernier blocage observé, en attente d'affichage. La page de login peut être montée
// après l'événement quand le blocage vient du dashboard : elle le consomme à son montage.
let lastBlockedAccess = null;

export function setupAxiosInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Traité avant la garde de session : un blocage en amont n'est pas une expiration
      // et ne doit pas être avalé par la temporisation de 5 secondes.
      if (isAccessBlocked(error)) {
        lastBlockedAccess = describeHttpError(error);
        window.dispatchEvent(
          new CustomEvent(ACCESS_BLOCKED_EVENT, { detail: lastBlockedAccess })
        );
        return Promise.reject(error);
      }

      if (isSessionExpired) {
        return Promise.reject(error);
      }

      if (isAuthenticationError(error)) {
        isSessionExpired = true;
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));

        setTimeout(() => {
          isSessionExpired = false;
        }, 5000);
      }

      return Promise.reject(error);
    }
  );
}

function isAuthenticationError(error) {
  if (!error.response) {
    return false;
  }

  const status = error.response.status;
  const data = error.response.data;

  if (status === 401) {
    return true;
  }

  // Code 4.1 = token invalide (convention EtherEgo ServicesError.js)
  if (data && data.success === false && data.data && data.data.code === 4.1) {
    return true;
  }

  return false;
}

function isIpDenied(error) {
  const data = error && error.response && error.response.data;

  return Boolean(data && data.data && data.data.code === IP_NOT_ALLOWED_CODE);
}

// Requête arrêtée avant admin-server, que le refus soit nommé par nginx ou réduit à un
// 403 au corps HTML : dans les deux cas réessayer ne sert à rien, contrairement à une
// indisponibilité passagère. La condition dérive de describeHttpError pour que les deux
// ne divergent pas.
export function isAccessBlocked(error) {
  return ["ip_denied", "forbidden"].includes(describeHttpError(error).kind);
}

// Lecture destructive : le blocage n'est rendu qu'une fois, null ensuite.
export function consumeBlockedAccess() {
  const blocked = lastBlockedAccess;
  lastBlockedAccess = null;

  return blocked;
}

// Traduit une erreur axios en message utilisateur. Reçoit l'erreur complète et non
// error.response, qui vaut undefined quand la requête n'a atteint aucun serveur.
export function describeHttpError(error) {
  const response = error && error.response;

  if (!response) {
    return {
      kind: "network",
      ip: null,
      message:
        "Le serveur est injoignable. Vérifiez votre connexion internet, puis réessayez.",
    };
  }

  if (isIpDenied(error)) {
    const ip = readDeniedIp(error);

    return { kind: "ip_denied", ip, message: ipDeniedMessage(ip) };
  }

  // 403 au corps HTML : refus nginx dont le motif n'est pas identifié. Le message reste
  // prudent, seul le code IP_NOT_ALLOWED autorise une affirmation sur la liste blanche.
  if (response.status === 403 && typeof response.data === "string") {
    return {
      kind: "forbidden",
      ip: null,
      message:
        "Accès refusé par le serveur. Vérifiez que votre adresse IP est autorisée à joindre l'administration EtherEgo.",
    };
  }

  if ([502, 503, 504].includes(response.status)) {
    return {
      kind: "unavailable",
      ip: null,
      message:
        "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
    };
  }

  // Code métier numérique : l'appelant sait le traduire, on ne fournit qu'un repli.
  const data = response.data;
  if (data && data.data && typeof data.data.code === "number") {
    return { kind: "business", ip: null, message: GENERIC_MESSAGE };
  }

  return { kind: "unknown", ip: null, message: GENERIC_MESSAGE };
}

function readDeniedIp(error) {
  const ip = error.response.data.data.ip;

  return isValidIp(ip) ? ip.trim() : null;
}

// L'IP vient du réseau et finit dans un contenu rendu en v-html par md-dialog-alert.
// Elle est validée ici, et échappée au moment de l'insertion.
function isValidIp(value) {
  if (typeof value !== "string") {
    return false;
  }

  const ip = value.trim();

  if (ip.length === 0 || ip.length > 45) {
    return false;
  }

  const ipv4 = ip.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (ipv4) {
    return ipv4.slice(1).every((octet) => Number(octet) <= 255);
  }

  return ip.includes(":") && /^[0-9a-fA-F:.]+$/.test(ip);
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function ipDeniedMessage(ip) {
  const address = ip
    ? "Votre adresse IP publique : <strong>" + escapeHtml(ip) + "</strong>"
    : "Vous pouvez relever votre adresse IP publique sur monip.org.";

  return (
    "Accès refusé : votre adresse IP n'est pas autorisée à joindre l'administration EtherEgo." +
    "<br /><br />" +
    address +
    "<br /><br />" +
    "Transmettez cette adresse à votre interlocuteur EtherEgo pour qu'elle soit ajoutée aux adresses autorisées."
  );
}

export function resetSessionExpiredState() {
  isSessionExpired = false;
}
